import { useApolloClient } from '@apollo/client';
import { appRoutes } from 'app/app-routes';
import ListingView from 'common/components/ListingView';
import { TNull } from 'common/types/t-null';
import { LISTINGS_SEARCH_QUERY } from 'data/ListingsSearchQuery.graphql';
import { Listing, ListingsSearchQuery, useListingsAddViewMutation } from 'graphql/generated';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import WsButton from 'stories/inputs/WsButton';
import WsFlexbox from 'stories/layout/WsFlexbox';
import WsGridContainer from 'stories/layout/WsGridContainer';

type RouteParams = 'id' | 'backLabel';

const ItemDetail = () => {
  const client = useApolloClient();
  const dispatch = useDispatch();
  const { id, backLabel } = useParams<Record<RouteParams, string>>();
  const navigate = useNavigate();
  const [listing, listingTo] = React.useState<Listing | undefined>(undefined);
  const [favorite, favoriteTo] = React.useState(false);
  const [runOnce, runOnceTo] = React.useState(true);
  const user = useSelector((state: RootState) => cacheSelectors.user(state));
  const viewIds = useSelector((state: RootState) => cacheSelectors.viewIds(state));
  const [addViewFn] = useListingsAddViewMutation();

  useEffect(() => {
    if (!id) navigate(appRoutes.errorPage);
    else {
      const doAddView = async () => {
        await addViewFn({ variables: { listingId: id } });
      };
      if (runOnce) {
        if (!viewIds.includes(id)) {
          dispatch(cacheActions.addView(id));
          doAddView();
        }
        runOnceTo(false);
        const findListing = async (id: string) => {
          const { data } = await client.query<ListingsSearchQuery>({
            query: LISTINGS_SEARCH_QUERY,
            variables: { filters: [{ field: 'id', strVal: id }] },
          });
          if (data?.listingsSearch) {
            const listing = data.listingsSearch.listings[0];
            listingTo(listing);
          }
          if (user?.favorites?.find(x => x.id === id)) favoriteTo(true);
        };
        findListing(id);
      }
    }
  }, [id, navigate, client, runOnce, user, dispatch, viewIds, addViewFn]);

  const getCustomFeature = (value: string, label: string, other?: TNull<string>) =>
    value === 'other' ? other || '' : label;

  return (
    <WsGridContainer>
      <WsFlexbox sx={{ paddingLeft: 0, width: '100%' }}>
        <WsButton label={`Back to ${backLabel}`} variant="text" onClick={() => navigate(-1)} />
      </WsFlexbox>
      <ListingView favorite={favorite} listing={listing} user={user} favoriteToggleFn={favoriteTo} />
    </WsGridContainer>
  );
};

export default ItemDetail;
